import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { formatDisplayName } from "@/shared/utils/formatters";
import {
  accessService,
  type UserSummary,
} from "@/features/admin/services/accessService";

// ─── Module config ────────────────────────────────────────────────────────────
interface ModuleDef {
  key: string;
  /** false → write-only module (no _read role exists) */
  hasRead: boolean;
}

const MODULES: ModuleDef[] = [
  { key: "announcements",   hasRead: true  },
  { key: "inquiries",       hasRead: true  },
  { key: "leave",           hasRead: true  },
  { key: "flight",          hasRead: true  },
  { key: "visa",            hasRead: true  },
  { key: "profile",         hasRead: true  },
  { key: "company",         hasRead: true  },
  { key: "sharepoint",      hasRead: true  },
  { key: "souser",          hasRead: true  }, // conditionally shown: bu_souser_enabled
  { key: "assessments",     hasRead: true  }, // conditionally shown: bu_assessment_enabled
  { key: "chat_template",   hasRead: false },
  { key: "upload_user_data", hasRead: false },
  { key: "role_management", hasRead: false }, // always at bottom
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const readRole = (mod: string) => `${mod}_read`;
const writeRole = (mod: string) => `${mod}_write`;

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Read-only badge list shown to users without role_management_write */
const SelfRolesView = ({ roles }: { roles: string[] }) => {
  const { t } = useTranslation();

  const roleLabel = (role: string) => {
    const mod = role.replace(/_read$|_write$/, "");
    const modLabel = t(`access.roles.modules.${mod}`, { defaultValue: mod });
    const suffix = role.endsWith("_write") ? t("access.roles.col_write") : t("access.roles.col_read");
    return `${modLabel} — ${suffix}`;
  };

  if (roles.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <svg
          className="w-12 h-12 mx-auto mb-3 opacity-40"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
        <p className="text-sm font-medium">{t("access.roles.no_roles_title")}</p>
        <p className="text-xs mt-1">{t("access.roles.no_roles_hint")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {roles.map((role) => (
        <div
          key={role}
          className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-100"
        >
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${role.endsWith("_write") ? "bg-indigo-500" : "bg-sky-400"}`}
          />
          <span className="text-sm text-indigo-800 font-medium">
            {roleLabel(role)}
          </span>
          <span
            className={`ml-auto text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${role.endsWith("_write") ? "bg-indigo-100 text-indigo-600" : "bg-sky-100 text-sky-600"}`}
          >
            {role.endsWith("_write") ? t("access.roles.col_write") : t("access.roles.col_read")}
          </span>
        </div>
      ))}
    </div>
  );
};

/** Role permission matrix with checkboxes (manager only) */
const RoleMatrix = ({
  modules,
  checkedRoles,
  onChange,
}: {
  modules: ModuleDef[];
  checkedRoles: Set<string>;
  onChange: (role: string, checked: boolean) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 pr-4 text-xs font-semibold uppercase tracking-wide text-gray-500 w-48">
              {t("access.roles.col_module")}
            </th>
            <th className="text-center py-2 px-4 text-xs font-semibold uppercase tracking-wide text-sky-600 w-24">
              {t("access.roles.col_read")}
            </th>
            <th className="text-center py-2 px-4 text-xs font-semibold uppercase tracking-wide text-indigo-600 w-24">
              {t("access.roles.col_write")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {modules.map((mod) => {
            const rRole = readRole(mod.key);
            const wRole = writeRole(mod.key);
            const writeChecked = checkedRoles.has(wRole);
            // read is considered checked if either _read or _write is in set
            const readChecked = checkedRoles.has(rRole) || writeChecked;
            // read checkbox is disabled when write is checked (write implies read)
            const readDisabled = writeChecked;

            return (
              <tr key={mod.key} className="hover:bg-gray-50 transition-colors">
                <td className="py-2.5 pr-4 font-medium text-gray-700">
                  {t(`access.roles.modules.${mod.key}`, { defaultValue: mod.key })}
                </td>

                {/* Read */}
                <td className="py-2.5 px-4 text-center">
                  {mod.hasRead ? (
                    <input
                      type="checkbox"
                      checked={readChecked}
                      disabled={readDisabled}
                      onChange={(e) => onChange(rRole, e.target.checked)}
                      className="w-4 h-4 rounded text-sky-500 border-gray-300 focus:ring-sky-400 disabled:opacity-50 cursor-pointer disabled:cursor-default"
                    />
                  ) : (
                    <span className="text-gray-300 text-lg leading-none">
                      —
                    </span>
                  )}
                </td>

                {/* Write */}
                <td className="py-2.5 px-4 text-center">
                  <input
                    type="checkbox"
                    checked={writeChecked}
                    onChange={(e) => onChange(wRole, e.target.checked)}
                    className="w-4 h-4 rounded text-indigo-500 border-gray-300 focus:ring-indigo-400 cursor-pointer"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export const RoleManagementPage = () => {
  const { t } = useTranslation();
  const { roles: myRoles, userData } = useAuth();
  const { canWrite, isFullAccess } = usePermissions();
  const isManager = canWrite("role_management");

  // Manager state
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [checkedRoles, setCheckedRoles] = useState<Set<string>>(new Set());
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // ── Fetch users (debounced) ───────────────────────────────────────────────
  useEffect(() => {
    if (!isManager) return;
    setLoadingUsers(true);
    const timer = setTimeout(async () => {
      try {
        const result = await accessService.getUsers(search);
        setUsers(result);
      } catch {
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, isManager]);

  // ── Select user → load their roles ───────────────────────────────────────
  const handleSelectUser = useCallback(async (user: UserSummary) => {
    setSelectedUser(user);
    setToast(null);
    setLoadingRoles(true);
    try {
      const roles = await accessService.getUserRoles(user.id);
      setCheckedRoles(new Set(roles));
    } catch {
      setCheckedRoles(new Set());
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  // ── Toggle a role checkbox ────────────────────────────────────────────────
  const handleToggle = useCallback((roleName: string, checked: boolean) => {
    setCheckedRoles((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(roleName);
        // write checked → also add the implied _read (if it exists for this module)
        if (roleName.endsWith("_write")) {
          const mod = MODULES.find((m) => writeRole(m.key) === roleName);
          if (mod?.hasRead) next.add(readRole(mod.key));
        }
      } else {
        next.delete(roleName);
        // read unchecked → also remove write (can't have write without read)
        if (roleName.endsWith("_read")) {
          next.delete(roleName.replace("_read", "_write"));
        }
      }
      return next;
    });
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setToast(null);
    try {
      await accessService.replaceUserRoles(selectedUser.id, [...checkedRoles]);
      setToast({ type: "success", text: t("access.roles.save_success") });
    } catch {
      setToast({ type: "error", text: t("access.roles.save_error") });
    } finally {
      setSaving(false);
    }
  };

  // ── Visible modules (filtered by BU feature flags) ───────────────────────
  const visibleModules = MODULES.filter((mod) => {
    if (mod.key === "souser")      return userData?.bu_souser_enabled === true;
    if (mod.key === "assessments") return userData?.bu_assessment_enabled === true;
    return true;
  });

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">
            {isManager ? t("access.roles.page_title_manager") : t("access.roles.page_title_self")}
          </h1>
          <p className="text-xs text-gray-500">
            {isManager
              ? t("access.roles.subtitle_manager")
              : t("access.roles.subtitle_self")}
          </p>
        </div>
      </div>

      {/* ── SELF VIEW (read-only) ── */}
      {!isManager && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-semibold text-gray-700">
              {formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
            </span>
            {isFullAccess && (
              <span className="text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                {t("access.roles.full_access")}
              </span>
            )}
          </div>
          <SelfRolesView roles={myRoles} />
        </div>
      )}

      {/* ── MANAGER VIEW ── */}
      {isManager && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left: User list */}
          <div className="md:col-span-1 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder={t("access.roles.search_placeholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-100 max-h-[420px]">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                  {t("access.roles.loading_users")}
                </div>
              ) : users.length === 0 ? (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                  {t("access.roles.no_users")}
                </div>
              ) : (
                users.map((user) => {
                  const isSelected = selectedUser?.id === user.id;
                  return (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className={`w-full text-left px-3 py-2.5 transition-colors ${
                        isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold uppercase flex-shrink-0 ${
                            isSelected
                              ? "bg-indigo-200 text-indigo-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {user.first_name?.[0]}
                          {user.last_name?.[0]}
                        </div>
                        <div className="min-w-0">
                          <p
                            className={`text-sm font-medium truncate ${isSelected ? "text-indigo-700" : "text-gray-800"}`}
                          >
                            {formatDisplayName(user.last_name, user.first_name, user.middle_name)}
                          </p>
                          <p className="text-[11px] text-gray-400 truncate">
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Role matrix */}
          <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col">
            {!selectedUser ? (
              <div className="flex flex-col items-center justify-center flex-1 py-20 text-gray-400">
                <svg
                  className="w-10 h-10 mb-3 opacity-40"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <p className="text-sm">{t("access.roles.select_user_hint")}</p>
              </div>
            ) : (
              <>
                {/* User header */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold uppercase flex-shrink-0">
                    {selectedUser.first_name?.[0]}
                    {selectedUser.last_name?.[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {formatDisplayName(selectedUser.last_name, selectedUser.first_name, selectedUser.middle_name)}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {selectedUser.email} · {selectedUser.user_type}
                    </p>
                  </div>
                  {checkedRoles.size === 0 && (
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      {t("access.roles.full_access_strategy")}
                    </span>
                  )}
                </div>

                {/* Matrix */}
                <div className="flex-1 px-5 py-4">
                  {loadingRoles ? (
                    <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
                      {t("access.roles.loading_roles")}
                    </div>
                  ) : (
                    <RoleMatrix
                      modules={visibleModules}
                      checkedRoles={checkedRoles}
                      onChange={handleToggle}
                    />
                  )}
                </div>

                {/* Footer: save + toast */}
                <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-3">
                  {toast && (
                    <span
                      className={`text-xs font-medium flex-1 ${toast.type === "success" ? "text-emerald-600" : "text-red-500"}`}
                    >
                      {toast.text}
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedUser(null);
                        setCheckedRoles(new Set());
                        setToast(null);
                      }}
                      className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {t("access.roles.cancel")}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || loadingRoles}
                      className="px-4 py-1.5 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? t("access.roles.saving") : t("access.roles.save")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
